'use client'

import { useState, useEffect, useCallback } from 'react'
import { createClient, isSupabaseConfigured } from '@/lib/supabase/client'
import { REFERENCE_PRICE, formatCreditCount } from '@/lib/pricing-display'
// Les limites annoncées DÉRIVENT des constantes qui les font appliquer. Écrites
// en dur, elles divergeaient : la landing promettait « 3 entretiens » quand le
// code en autorise 5, et « 30 minutes » quand le forfait en couvre 60.
import { FREE_PLAN, PLAN_CREDITS, PRO_PERIOD_DAYS } from '@/lib/credit-costs'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  PencilLine,
  Mic,
  Tags,
  Brain,
  Shield,
  Lock,
  Wallet,
  LogOut,
  History,
  User,
  Loader2,
  BarChart3,
  CreditCard,
  CalendarDays,
  ArrowUpRight,
  ArrowDownLeft,
  Activity,
  TrendingUp,
  ArrowRight,
  Check,
  Menu,
  ChevronDown,
  Settings,
  Linkedin,
  MessageCircle,
  Phone,
  FolderOpen,
  Clock,
  Download,
} from 'lucide-react'
import { toast } from 'sonner'
import ModuleNoteTechnique from '@/components/transqual/module-note-technique'
import Module01 from '@/components/transqual/module-01'
import { Z } from '@/lib/z-index'
import Module02 from '@/components/transqual/module-02'
import Module03 from '@/components/transqual/module-03'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import CreditsWidget from '@/components/CreditsWidget'
import HeroSection from '@/components/HeroSection'
import ModulesSection from '@/components/ModulesSection'
import { FreemiumProvider } from '@/lib/freemium-context'
import AuthModal from '@/components/AuthModal'
import SignupGateModal from '@/components/SignupGateModal'
import UpgradeModal from '@/components/UpgradeModal'
import UserPanel from '@/components/UserPanel'
import DashboardSheet from '@/components/dashboard/DashboardSheet'
import type { DashboardTab } from '@/components/dashboard/types'
import AdminPanel from '@/components/AdminPanel'
import OnboardingTour from '@/components/OnboardingTour'
import GuideDownloadButton from '@/components/GuideDownloadButton'
import GuideViewerButton from '@/components/GuideViewerButton'

const FaqSection = dynamic(() => import('@/components/FaqSection'))

// ── Types ────────────────────────────────────────────────────────────

interface UserProfile {
  id: string
  email: string
  full_name: string | null
  credits: number
  plan: string
  created_at?: string
}

interface Transaction {
  id: string
  amount: number
  type: string
  description: string | null
  created_at: string
}

interface UsageStat {
  module: string
  label: string
  count: number
  total_credits: number
}

interface DashboardStats {
  stats: UsageStat[]
  total_spent: number
  total_operations: number
  member_since: string | null
}

interface SavedTranscription {
  id: string
  file_name: string | null
  statut: string
  word_count: number | null
  created_at: string
  texte: string | null
  mode: string | null
}

// ── Constants ────────────────────────────────────────────────────────

const MODULE_LABELS: Record<string, string> = {
  transcription: 'Transcription',
  correction: 'Correction IA',
  coding: 'Codification',
  analysis: 'Analyse qualitative',
  'note-tech': 'Note technique',
  bonus: 'Bonus de bienvenue',
  purchase: 'Achat de crédits',
}

const MODULE_ICONS: Record<string, string> = {
  transcription: '🎤',
  correction: '✏️',
  coding: '🏷️',
  analysis: '🧠',
  'note-tech': '📋',
  bonus: '🎁',
  purchase: '💳',
}

const MODULE_CARDS = [
  { emoji: '✏️', title: 'Note Technique', desc: 'Proposition technique qualitative à partir du TDR', value: 'note-technique' },
  { emoji: '🎤', title: 'Transcription', desc: 'Audio/vidéo vers verbatim corrigé', value: 'module-01' },
  { emoji: '🏷️', title: 'Codification', desc: 'Analyse thématique automatisée', value: 'module-02' },
  { emoji: '🧠', title: 'Analyse', desc: 'Rapports d\'analyse qualitatifs', value: 'module-03' },
]

const WHY_CARDS = [
  {
    num: '01', color: '#2d5a27', bg: '#e8f0e2', borderColor: '#c5ddb8',
    title: "Moins de saisie, plus d'efficacité.",
    desc: "La transcription et la codification automatisées réduisent fortement le temps passé sur les tâches répétitives.",
    icon: (<><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></>),
  },
  {
    num: '02', color: '#1a4a7a', bg: '#e8f0f8', borderColor: '#b5d4f4',
    title: "Des livrables professionnels.",
    desc: "Produisez des documents prêts pour vos clients, vos partenaires ou vos travaux académiques.",
    icon: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/></>),
  },
  {
    num: '03', color: '#1a6060', bg: '#e8f5f5', borderColor: '#9fe1cb',
    title: "Un workflow complet.",
    desc: "De l'entretien au rapport final, tout reste dans un seul espace de travail.",
    icon: (<><rect x="3" y="3" width="6" height="6" rx="1"/><rect x="15" y="3" width="6" height="6" rx="1"/><rect x="9" y="15" width="6" height="6" rx="1"/><path d="M6 9v2c0 1.7 1.3 3 3 3h6c1.7 0 3-1.3 3-3V9"/></>),
  },
  {
    num: '04', color: '#6a3500', bg: '#fdf3e8', borderColor: '#f5c4a0',
    title: "Pensée pour le terrain.",
    desc: "Une approche conçue pour les besoins réels des études qualitatives.",
    icon: (<><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></>),
  },
]

const AUDIENCE_CARDS = [
  {
    color: '#2d5a27', bg: '#e8f0e2', borderColor: '#c5ddb8',
    title: 'Chercheurs en sciences sociales',
    desc: 'Sociologues, anthropologues, politologues',
    icon: (<><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 1 0-16 0"/><path d="M15 11l2 8-5-2-5 2 2-8"/></>),
  },
  {
    color: '#1a4a7a', bg: '#e8f0f8', borderColor: '#b5d4f4',
    title: 'Doctorants & Étudiants Master',
    desc: 'Thèses, mémoires, travaux académiques',
    icon: (<><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></>),
  },
  {
    color: '#6a3500', bg: '#fdf3e8', borderColor: '#f5c4a0',
    title: "Consultants & Bureaux d'étude",
    desc: 'Missions terrain, rapports clients',
    icon: (<><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>),
  },
  {
    color: '#1a6060', bg: '#e8f5f5', borderColor: '#9fe1cb',
    title: 'ONG & Organisations terrain',
    desc: 'Rapports de projet, évaluations participatives',
    icon: (<><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>),
  },
  {
    color: '#5a2d82', bg: '#f3eefb', borderColor: '#d4b5f4',
    title: 'Responsables Suivi & Évaluation',
    desc: 'S&E, impact, redevabilité, indicateurs',
    icon: (<><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>),
  },
  {
    color: '#b85c00', bg: '#fff3e8', borderColor: '#f5c870',
    title: "Instituts de sondage & d'analyse",
    desc: 'Études qualitatives à grande échelle',
    icon: (<><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></>),
  },
]

const CMP_ROWS: { label: string; qualyse: string; manuel: string; nvivo: string }[] = [
  { label: 'Note technique automatique depuis TDR',              qualyse: 'yes', manuel: 'no',  nvivo: 'no'      },
  { label: 'Transcription audio multilingue',                   qualyse: 'yes', manuel: 'no',  nvivo: 'partial' },
  { label: 'Correction et harmonisation du verbatim',           qualyse: 'yes', manuel: 'no',  nvivo: 'no'      },
  { label: 'Codification inductive, déductive et mixte auto',   qualyse: 'yes', manuel: 'no',  nvivo: 'no'      },
  { label: 'Rédaction de rapport complet',                      qualyse: 'yes', manuel: 'no',  nvivo: 'partial' },
  { label: 'Export DOCX / XLSX / TXT',                          qualyse: 'yes', manuel: 'no',  nvivo: 'yes'     },
  { label: 'Licence requise',                                   qualyse: 'no',  manuel: 'no',  nvivo: 'yes'     },
  { label: "Simplicité d'utilisation",                          qualyse: 'yes', manuel: 'no',  nvivo: 'partial' },
  { label: 'Rapidité de traitement',                            qualyse: 'yes', manuel: 'no',  nvivo: 'partial' },
]

const CmpIconYes = () => (
  <div style={{ width:18, height:18, borderRadius:'50%', background:'#2d5a27', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 7l3 3 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
  </div>
)
const CmpIconNo = () => (
  <div style={{ width:18, height:18, borderRadius:'50%', background:'#f0ede6', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="#b8b8b8" strokeWidth="1.8" strokeLinecap="round"/></svg>
  </div>
)
const CmpIconPartial = () => (
  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
    <div style={{ width:18, height:18, borderRadius:'50%', background:'#fdf3e8', border:'1.5px solid #f5c4a0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12 }}>~</div>
    <span style={{ fontSize:10, fontWeight:700, color:'#c08a30', letterSpacing:'0.03em' }}>partiel</span>
  </div>
)

// (pricing plans rendered inline in the tarification section)

// ── Main Page ────────────────────────────────────────────────────────

export default function Home() {
  const [activeTab, setActiveTab] = useState('module-01')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [featuresOpen, setFeaturesOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authModalTab, setAuthModalTab] = useState<'login' | 'register'>('register')
  const [authRedirectAfterSuccess, setAuthRedirectAfterSuccess] = useState<string | undefined>(undefined)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showGateModal, setShowGateModal] = useState(false)
  const [showAdminPanel, setShowAdminPanel] = useState(false)
  const [user, setUser] = useState<{ email: string } | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loadingProfile, setLoadingProfile] = useState(true)
  const [showDashboard, setShowDashboard] = useState(false)
  const [dashboardInitialTab, setDashboardInitialTab] = useState<DashboardTab>('overview')
  // Mode immersif mobile : un module est ouvert en plein écran (drill-in) →
  // on masque le Hero et les sections au-dessus pour focaliser sur l'upload.
  const [moduleImmersive, setModuleImmersive] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)

  const supabase = createClient()

  // ── Navigation handlers ──────────────────────────────────────────

  const scrollToSection = useCallback((id: string) => {
    setMobileMenuOpen(false)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const scrollToModules = useCallback(() => {
    setMobileMenuOpen(false)
    document.getElementById('comment-ca-marche')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // ── Supabase auth & profile ──────────────────────────────────────

  const fetchProfile = useCallback(async () => {
    if (!isSupabaseConfigured || !supabase) {
      setLoadingProfile(false)
      return
    }
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return
      setUser({ email: authUser.email || '' })

      const res = await fetch('/api/credits')
      if (res.ok) {
        const data = await res.json()
        setProfile(data.profile)
      }
    } catch {
      // Silent
    } finally {
      setLoadingProfile(false)
    }
  }, [supabase])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  // ── Dashboard ────────────────────────────────────────────────────
  // Le tableau de bord est désormais autonome (composant DashboardSheet) :
  // il charge ses propres données via l'endpoint consolidé /api/dashboard.
  // Ici on ne gère que l'ouverture et l'onglet cible.

  const openDashboard = (tab: DashboardTab = 'overview') => {
    setDashboardInitialTab(tab)
    setShowDashboard(true)
  }

  const goToPricing = () => {
    setShowDashboard(false)
    document.getElementById('tarification')?.scrollIntoView({ behavior: 'smooth' })
  }

  const openAuthModal = (tab: 'login' | 'register' = 'register', redirect?: string) => {
    setAuthModalTab(tab)
    setAuthRedirectAfterSuccess(redirect)
    setShowAuthModal(true)
  }

  const handleAuthSuccess = () => {
    fetchProfile()
  }

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      // signOut CÔTÉ CLIENT : efface la session ET émet l'événement SIGNED_OUT.
      // C'est ce qui remet isAuthenticated à false dans le FreemiumProvider
      // (via onAuthStateChange) → le gate de connexion se réaffiche avant tout
      // traitement. Sans ça, isAuthenticated restait périmé à true après le
      // logout serveur, et un nouveau « Lancer » partait puis échouait en 401.
      if (supabase) await supabase.auth.signOut()
      // Filet serveur — purge les cookies de session côté serveur.
      await fetch('/api/auth/logout', { method: 'POST' })
      setUser(null)
      setProfile(null)
      toast.success('Déconnexion réussie.')
    } catch {
      toast.error('Erreur lors de la déconnexion.')
    } finally {
      setLoggingOut(false)
    }
  }

  // ── Formatters ──────────────────────────────────────────────────

  const formatCredits = (amount: number) => `${Math.abs(amount).toFixed(2)} $`

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  // ── Derived values ──────────────────────────────────────────────

  const displayCredits = profile ? `${Math.round(profile.credits).toLocaleString('fr-FR')} crédits` : '—'
  const displayPlan = profile?.plan === 'pro' ? 'Pro' : 'Gratuit'

  // ── Tab definitions ─────────────────────────────────────────────

  const MODULE_TABS = [
    { value: 'note-technique', icon: PencilLine, short: 'Note Tech.', full: 'Module 01' },
    { value: 'module-01', icon: Mic, short: 'Transcription', full: 'Module 02' },
    { value: 'module-02', icon: Tags, short: 'Codification', full: 'Module 03' },
    { value: 'module-03', icon: Brain, short: 'Analyse', full: 'Module 04' },
  ]

  return (
    <FreemiumProvider
      onOpenUpgrade={() => setShowUpgradeModal(true)}
      onOpenAuth={openAuthModal}
      onOpenAuthGate={() => setShowGateModal(true)}
      // Dette n°9 — le solde de l'en-tête suit désormais le solde serveur.
      // `incrementLimit` rafraîchit déjà les limites après chaque opération des
      // quatre modules, et `/api/limits` renvoie `credits` : il n'y a donc
      // aucune requête ajoutée, seulement la diffusion d'une valeur déjà lue.
      // Même forme que `onCreditsLoaded` de `DashboardSheet`, plus bas.
      onCreditsChange={(c) => setProfile((p) => (p ? { ...p, credits: c } : p))}
    >
    <div className="min-h-screen flex flex-col bg-tq-bg">
      {/* ═══════════════════════════════════════════════════════════ */}
      {/* HEADER */}
      <header style={{background:'#fff',borderBottom:'1px solid #E5E7EB',boxShadow:'0 1px 4px rgba(0,0,0,.07)',position:'sticky',top:0,zIndex:Z.header}}>
        <style>{`
          @media(max-width:1023px){.qnav-desk{display:none!important;}.qnav-burger{display:flex!important;}}
          @media(min-width:1024px){.qnav-burger{display:none!important;}}
          .qnav-link{padding:8px 12px;border-radius:8px;font-size:14px;font-weight:500;color:#374151;background:none;border:none;cursor:pointer;transition:background .12s;white-space:nowrap;}
          .qnav-link:hover{background:#F9FAFB;color:#111827;}
        `}</style>
        <div style={{maxWidth:1200,margin:'0 auto',padding:'0 28px',height:64,display:'flex',alignItems:'center',justifyContent:'space-between',gap:16}}>

          {/* Logo */}
          <a href="/" style={{display:'flex',alignItems:'center',gap:10,textDecoration:'none',flexShrink:0}}>
            <Image src="/qualyse-logo.webp" alt="Qualyse" width={36} height={36} style={{borderRadius:8}} priority />
            <span style={{fontSize:18,fontWeight:800,color:'#111827',fontFamily:'Georgia,serif',letterSpacing:'-0.3px'}}>Qualyse</span>
          </a>

          {/* Center nav — desktop */}
          <nav className="qnav-desk" style={{display:'flex',alignItems:'center',gap:2,flex:1,justifyContent:'center'}}>

            {/* Fonctionnalités dropdown */}
            <div style={{position:'relative'}}
              onMouseEnter={() => setFeaturesOpen(true)}
              onMouseLeave={() => setFeaturesOpen(false)}>
              <button className="qnav-link" style={{display:'flex',alignItems:'center',gap:5}}>
                Fonctionnalités
                <ChevronDown size={13} style={{transition:'transform .15s',transform:featuresOpen?'rotate(180deg)':'none'}} />
              </button>
              {featuresOpen && (
                <div style={{position:'absolute',top:'calc(100% + 4px)',left:'50%',transform:'translateX(-50%)',background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,boxShadow:'0 12px 32px rgba(0,0,0,.12)',padding:'6px',minWidth:250,zIndex:Z.dropdown}}>
                  {([
                    {icon:'📋',label:'Note Technique',sub:'Proposition technique depuis un TDR'},
                    {icon:'🎙️',label:'Transcription & Correction',sub:'Audio / vidéo vers verbatim corrigé'},
                    {icon:'🏷️',label:'Codification thématique',sub:'Matrice NVivo / MAXQDA automatique'},
                    {icon:'📊',label:"Rapport d'analyse",sub:'4 méthodes qualitatives'},
                  ] as {icon:string,label:string,sub:string}[]).map(item => (
                    <button key={item.label}
                      onClick={() => { setFeaturesOpen(false); scrollToSection('comment-ca-marche'); }}
                      style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',borderRadius:8,width:'100%',textAlign:'left',background:'none',border:'none',cursor:'pointer',transition:'background .12s'}}
                      onMouseEnter={e => (e.currentTarget.style.background='#F0FAF4')}
                      onMouseLeave={e => (e.currentTarget.style.background='none')}>
                      <span style={{fontSize:18,lineHeight:'1',flexShrink:0}}>{item.icon}</span>
                      <div>
                        <p style={{fontSize:13,fontWeight:600,color:'#111827',margin:0}}>{item.label}</p>
                        <p style={{fontSize:11,color:'#6B7280',margin:'1px 0 0'}}>{item.sub}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button className="qnav-link" onClick={() => scrollToSection('tarification')}>Tarifs</button>
            <a href="/contact" className="qnav-link" style={{textDecoration:'none',display:'inline-block'}}>Contact</a>

          </nav>

          {/* Right side */}
          <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>

            {!user ? (
              <>
                <button onClick={() => openAuthModal('login')} className="qnav-desk"
                  style={{padding:'8px 18px',borderRadius:8,fontSize:14,fontWeight:600,color:'#1B4332',background:'transparent',border:'1.5px solid #1B4332',whiteSpace:'nowrap',transition:'background .15s',cursor:'pointer'}}
                  onMouseEnter={e => (e.currentTarget.style.background='#F0FAF4')}
                  onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
                  Se connecter
                </button>
                <button onClick={() => openAuthModal('register')}
                  className="qnav-cta-register"
                  style={{padding:'8px 18px',borderRadius:8,fontSize:14,fontWeight:600,color:'#fff',background:'#1B4332',border:'1.5px solid #1B4332',whiteSpace:'nowrap',transition:'background .15s',cursor:'pointer'}}
                  onMouseEnter={e => (e.currentTarget.style.background='#163d28')}
                  onMouseLeave={e => (e.currentTarget.style.background='#1B4332')}>
                  Commencer gratuitement
                </button>
              </>
            ) : (
              <>
                {/* Credits balance */}
                <button id="tour-credits" onClick={() => openDashboard('overview')}
                  style={{display:'flex',alignItems:'center',gap:6,padding:'7px 14px',borderRadius:8,background:'#F0FAF4',border:'1px solid #c8e6d0',cursor:'pointer',transition:'background .15s',whiteSpace:'nowrap',flexShrink:0}}
                  onMouseEnter={e => (e.currentTarget.style.background='#D8F3DC')}
                  onMouseLeave={e => (e.currentTarget.style.background='#F0FAF4')}>
                  <Wallet size={14} color="#1B4332" />
                  {loadingProfile
                    ? <Loader2 size={13} className="animate-spin" color="#1B4332" />
                    : <span style={{fontSize:13,fontWeight:700,color:'#1B4332'}}>{displayCredits}</span>
                  }
                </button>

                {/* Avatar + dropdown */}
                <div style={{position:'relative'}}>
                  <button onClick={() => setUserMenuOpen(v => !v)}
                    style={{display:'flex',alignItems:'center',gap:7,padding:'5px 10px 5px 5px',borderRadius:8,border:userMenuOpen?'1px solid #1B4332':'1px solid #E5E7EB',background:'#fff',cursor:'pointer',transition:'border-color .15s'}}
                    onMouseEnter={e => (e.currentTarget.style.borderColor='#1B4332')}
                    onMouseLeave={e => { if (!userMenuOpen) e.currentTarget.style.borderColor='#E5E7EB'; }}>
                    <div style={{width:28,height:28,borderRadius:'50%',background:'#1B4332',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      <User size={13} color="#fff" />
                    </div>
                    <span style={{fontSize:12,fontWeight:500,color:'#374151',maxWidth:110,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{user.email.split('@')[0]}</span>
                    <ChevronDown size={13} color="#9CA3AF" style={{transition:'transform .15s',transform:userMenuOpen?'rotate(180deg)':'none',flexShrink:0}} />
                  </button>
                  {userMenuOpen && (
                    <>
                      <div style={{position:'fixed',inset:0,zIndex:Z.dropdownBackdrop}} onClick={() => setUserMenuOpen(false)} />
                      <div style={{position:'absolute',top:'calc(100% + 6px)',right:0,background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,boxShadow:'0 12px 32px rgba(0,0,0,.12)',padding:'6px',minWidth:196,zIndex:Z.dropdown}}>
                        <div style={{padding:'8px 12px 10px',borderBottom:'1px solid #F3F4F6',marginBottom:4}}>
                          <p style={{fontSize:11,color:'#6B7280',margin:0,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{user.email}</p>
                        </div>
                        <button onClick={() => { openDashboard('profil'); setUserMenuOpen(false); }}
                          style={{display:'flex',alignItems:'center',gap:8,padding:'9px 12px',borderRadius:8,width:'100%',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:13,fontWeight:500,color:'#111827',transition:'background .12s'}}
                          onMouseEnter={e => (e.currentTarget.style.background='#F9FAFB')}
                          onMouseLeave={e => (e.currentTarget.style.background='none')}>
                          <User size={14} />
                          Mon profil
                        </button>
                        <button onClick={() => { openDashboard('profil'); setUserMenuOpen(false); }}
                          style={{display:'flex',alignItems:'center',gap:8,padding:'9px 12px',borderRadius:8,width:'100%',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:13,fontWeight:500,color:'#111827',transition:'background .12s'}}
                          onMouseEnter={e => (e.currentTarget.style.background='#F9FAFB')}
                          onMouseLeave={e => (e.currentTarget.style.background='none')}>
                          <Settings size={14} />
                          Paramètres
                        </button>
                        <div style={{borderTop:'1px solid #F3F4F6',margin:'4px 0'}} />
                        <button onClick={() => { handleLogout(); setUserMenuOpen(false); }} disabled={loggingOut}
                          style={{display:'flex',alignItems:'center',gap:8,padding:'9px 12px',borderRadius:8,width:'100%',textAlign:'left',background:'none',border:'none',cursor:loggingOut?'wait':'pointer',fontSize:13,fontWeight:500,color:'#DC2626',transition:'background .12s'}}
                          onMouseEnter={e => (e.currentTarget.style.background='#FEF2F2')}
                          onMouseLeave={e => (e.currentTarget.style.background='none')}>
                          {loggingOut ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
                          Déconnexion
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* Mobile burger */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="qnav-burger"
              style={{display:'none',padding:'7px',borderRadius:8,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',alignItems:'center',justifyContent:'center'}}>
              <Menu size={20} color="#374151" />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div style={{borderTop:'1px solid #E5E7EB',background:'#fff',boxShadow:'0 8px 24px rgba(0,0,0,.10)',padding:'12px 24px 20px',display:'flex',flexDirection:'column',gap:2}}>
            <button onClick={() => scrollToSection('comment-ca-marche')}
              style={{padding:'10px 12px',borderRadius:8,fontSize:14,fontWeight:500,color:'#374151',background:'none',border:'none',cursor:'pointer',textAlign:'left'}}
              onMouseEnter={e => (e.currentTarget.style.background='#F9FAFB')}
              onMouseLeave={e => (e.currentTarget.style.background='none')}>
              Fonctionnalités
            </button>
            <button onClick={() => scrollToSection('tarification')}
              style={{padding:'10px 12px',borderRadius:8,fontSize:14,fontWeight:500,color:'#374151',background:'none',border:'none',cursor:'pointer',textAlign:'left'}}
              onMouseEnter={e => (e.currentTarget.style.background='#F9FAFB')}
              onMouseLeave={e => (e.currentTarget.style.background='none')}>
              Tarifs
            </button>
            <a href="/contact" style={{padding:'10px 12px',borderRadius:8,fontSize:14,fontWeight:500,color:'#374151',textDecoration:'none',display:'block'}}
              onMouseEnter={e => (e.currentTarget.style.background='#F9FAFB')}
              onMouseLeave={e => (e.currentTarget.style.background='none')}>
              Contact
            </a>
            <div style={{height:1,background:'#F3F4F6',margin:'8px 0'}} />
            {!user ? (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                <button onClick={() => { setMobileMenuOpen(false); openAuthModal('login'); }} style={{display:'block',padding:'11px 16px',borderRadius:8,fontSize:14,fontWeight:600,color:'#1B4332',border:'1.5px solid #1B4332',background:'transparent',cursor:'pointer',textAlign:'center'}}>
                  Se connecter
                </button>
                <button onClick={() => { setMobileMenuOpen(false); openAuthModal('register'); }} style={{display:'block',padding:'11px 16px',borderRadius:8,fontSize:14,fontWeight:600,color:'#fff',background:'#1B4332',border:'1.5px solid #1B4332',cursor:'pointer',textAlign:'center'}}>
                  Commencer gratuitement
                </button>
              </div>
            ) : (
              <button onClick={() => { openDashboard('overview'); setMobileMenuOpen(false); }}
                style={{display:'flex',alignItems:'center',gap:8,padding:'10px 12px',borderRadius:8,fontSize:14,fontWeight:500,color:'#374151',background:'none',border:'none',cursor:'pointer',textAlign:'left'}}>
                <User size={15} />
                {user.email}
              </button>
            )}
          </div>
        )}
      </header>

      {/* SEO — H1 principal masqué visuellement, indexé par Google */}
      <h1 style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
        Analyse qualitative assistée par IA — Transcription, Codification, Note Technique et Rapports d&apos;analyse
      </h1>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 1. HERO SECTION                                              */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {/* Masqué quand un module est ouvert en plein écran sur mobile. */}
      {!moduleImmersive && (
        <HeroSection onScrollToModules={scrollToModules} onOpenAuth={openAuthModal} />
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 2. 4 MODULES INDÉPENDANTS                                    */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <ModulesSection profile={profile} onMobileViewChange={setModuleImmersive} />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 3. POURQUOI QUALYSE                                          */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section style={{ background: '#f7f5f0', borderBottom: '1px solid #e8e4dc' }}>
        <style>{`
          .why-card { transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease; }
          .why-card:hover { transform: translateY(-5px); box-shadow: 0 14px 36px rgba(0,0,0,0.10); }
          .why-card:hover .why-icon { transform: scale(1.12) rotate(-5deg); }
          .why-icon { transition: transform 0.22s ease; display:flex; align-items:center; justify-content:center; }
          @media (max-width: 640px) { .why-grid { grid-template-columns: 1fr !important; } }
        `}</style>
        <div className="qly-lp-inner" style={{ maxWidth: 1100, margin: '0 auto', padding: '72px 40px' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <span style={{
              display: 'inline-block', padding: '5px 14px',
              background: '#e8f0e2', color: '#2d5a27', borderRadius: 100,
              fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
              letterSpacing: '0.07em', textTransform: 'uppercase',
              marginBottom: 18,
            }}>
              Pourquoi Qualyse
            </span>
            <h2 style={{
              fontSize: 'clamp(24px, 2.5vw, 36px)', fontWeight: 800,
              color: '#1a1a1a', fontFamily: 'Georgia, serif',
              letterSpacing: '-0.4px', lineHeight: 1.2, margin: 0,
            }}>
              Pensé pour accélérer votre{' '}
              <em style={{ color: '#2d5a27', fontStyle: 'italic' }}>travail de recherche</em>
            </h2>
          </div>

          {/* 2×2 grid */}
          <div className="why-grid qly-why-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 20 }}>
            {WHY_CARDS.map((card, i) => (
              <div
                key={i}
                className="why-card"
                style={{
                  background: '#fff',
                  border: `1.5px solid ${card.borderColor}`,
                  borderRadius: 18,
                  padding: '28px 26px',
                  display: 'flex', gap: 22, alignItems: 'flex-start',
                  position: 'relative', overflow: 'hidden',
                }}
              >
                {/* Faint decorative number */}
                <div style={{
                  position: 'absolute', top: 14, right: 18,
                  fontSize: 38, fontWeight: 900, fontFamily: 'monospace',
                  color: card.color, opacity: 0.06, lineHeight: 1,
                  userSelect: 'none', pointerEvents: 'none',
                }}>
                  {card.num}
                </div>

                {/* Icon */}
                <div className="why-icon" style={{
                  width: 58, height: 58, borderRadius: 16, flexShrink: 0,
                  background: card.bg, border: `1.5px solid ${card.borderColor}`,
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke={card.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    {card.icon}
                  </svg>
                </div>

                {/* Content */}
                <div style={{ flex: 1, paddingTop: 2 }}>
                  <h3 style={{
                    fontSize: 16, fontWeight: 800, color: '#1a1a1a',
                    marginBottom: 8, lineHeight: 1.3,
                    fontFamily: 'Arial, Helvetica, sans-serif',
                  }}>
                    {card.title}
                  </h3>
                  <p style={{ fontSize: 14, color: '#6b6560', lineHeight: 1.72, margin: 0 }}>
                    {card.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 4. POUR QUI                                                  */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section style={{ background: '#fff', borderBottom: '1px solid #e8e4dc' }}>
        <style>{`
          .ac-card { position: relative; overflow: hidden; transition: transform 0.2s ease, box-shadow 0.2s ease; }
          .ac-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--ac); border-radius: 4px 0 0 4px; opacity: 0; transition: opacity 0.2s ease; }
          .ac-card:hover { transform: translateX(4px); box-shadow: 0 4px 18px rgba(0,0,0,0.09); }
          .ac-card:hover::before { opacity: 1; }
          .ac-icon { transition: transform 0.2s ease; display:flex; align-items:center; justify-content:center; }
          .ac-card:hover .ac-icon { transform: scale(1.1); }
          @keyframes ac-blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
          .dot-blink { animation: ac-blink 1.6s ease-in-out infinite; }
          @media (max-width: 768px) { .audience-layout { flex-direction: column !important; } .audience-photos { width: 100% !important; } }
        `}</style>
        <div className="qly-lp-inner" style={{ maxWidth: 1100, margin: '0 auto', padding: '72px 40px' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <span style={{
              display: 'inline-block', padding: '5px 14px',
              background: '#e8f0e2', color: '#2d5a27', borderRadius: 100,
              fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
              letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 18,
            }}>
              Pour qui
            </span>
            <h2 style={{
              fontSize: 'clamp(24px, 2.5vw, 36px)', fontWeight: 800,
              color: '#1a1a1a', fontFamily: 'Georgia, serif',
              letterSpacing: '-0.4px', lineHeight: 1.2, margin: 0,
            }}>
              Conçu pour la{' '}
              <em style={{ color: '#2d5a27', fontStyle: 'italic' }}>recherche qualitative</em>
            </h2>
          </div>

          {/* 2-col: photos left + cards right */}
          <div className="audience-layout" style={{ display: 'flex', gap: 48, alignItems: 'flex-start' }}>

            {/* Left: deux photos côte à côte */}
            <div className="audience-photos" style={{ width: 360, flexShrink: 0, position: 'relative' }}>

              <div style={{ display: 'flex', gap: 10 }}>
                {/* Photo 1 */}
                <div style={{ position: 'relative', flex: 1, borderRadius: 16, overflow: 'hidden', height: 260 }}>
                  <Image src="/terrain-photo.webp" alt="Chercheur en recherche qualitative terrain" fill style={{ objectFit: 'cover' }} loading="lazy" sizes="(max-width: 768px) 50vw, 180px" />
                  <div style={{
                    position: 'absolute', bottom: 10, left: 8, right: 8,
                    background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(8px)',
                    borderRadius: 8, padding: '6px 10px',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span className="dot-blink" style={{ width: 7, height: 7, borderRadius: '50%', background: '#4caf50', flexShrink: 0, display: 'inline-block' }} />
                    <span style={{ color: '#fff', fontSize: 10, fontWeight: 600 }}>Terrain — Abidjan</span>
                  </div>
                </div>

                {/* Photo 2 */}
                <div style={{ position: 'relative', flex: 1, borderRadius: 16, overflow: 'hidden', height: 260 }}>
                  <Image
                    src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=400&q=80"
                    alt="Focus group entretien qualitatif"
                    fill
                    style={{ objectFit: 'cover' }}
                    loading="lazy"
                    sizes="(max-width: 768px) 50vw, 180px"
                  />
                  <div style={{
                    position: 'absolute', bottom: 10, left: 8, right: 8,
                    background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(8px)',
                    borderRadius: 8, padding: '6px 10px',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span className="dot-blink" style={{ width: 7, height: 7, borderRadius: '50%', background: '#4caf50', flexShrink: 0, display: 'inline-block' }} />
                    <span style={{ color: '#fff', fontSize: 10, fontWeight: 600 }}>Focus group — Dakar</span>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              {/* Badge « 🌍 Utilisé dans 50 pays » RETIRÉ : aucune mesure
                  d'audience n'existe, et le chiffre était écrit en dur. Rien ne
                  le remplace — une affirmation d'usage ne peut pas être
                  remplacée par une autre affirmation d'usage. */}
            </div>

            {/* Right: 2×3 audience cards */}
            <div className="audience-cards-grid" style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {AUDIENCE_CARDS.map((card, i) => (
                <div
                  key={i}
                  className="ac-card"
                  style={{
                    '--ac': card.color,
                    background: card.bg,
                    border: `1.5px solid ${card.borderColor}`,
                    borderRadius: 14,
                    padding: '18px 16px 18px 20px',
                    display: 'flex', gap: 14, alignItems: 'flex-start',
                  } as React.CSSProperties}
                >
                  <div className="ac-icon" style={{
                    width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                    background: '#fff', border: `1.5px solid ${card.borderColor}`,
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                      stroke={card.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      {card.icon}
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a1a', lineHeight: 1.3, marginBottom: 4 }}>
                      {card.title}
                    </div>
                    <div style={{ fontSize: 11, color: '#7a7570', lineHeight: 1.5 }}>
                      {card.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 5. COMPARAISON                                               */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section id="demo" style={{ background: '#f4f1ea', borderBottom: '1px solid #e8e4dc' }}>
        <style>{`
          .cmp-row { opacity:0; transform:translateX(-6px); animation:cmpRowIn 0.4s ease forwards; }
          .cmp-row:nth-child(1){animation-delay:0.05s} .cmp-row:nth-child(2){animation-delay:0.10s}
          .cmp-row:nth-child(3){animation-delay:0.15s} .cmp-row:nth-child(4){animation-delay:0.20s}
          .cmp-row:nth-child(5){animation-delay:0.25s} .cmp-row:nth-child(6){animation-delay:0.30s}
          .cmp-row:nth-child(7){animation-delay:0.35s} .cmp-row:nth-child(8){animation-delay:0.40s}
          .cmp-row:nth-child(9){animation-delay:0.45s}
          @keyframes cmpRowIn { to { opacity:1; transform:none; } }
          .cmp-row:hover { background:#faf9f7 !important; }
          .cmp-row:hover .cmp-qcell { background:#f5f9f3 !important; }
          .cmp-qhead { position:relative; }
          .cmp-qhead::after { content:'⭐ Recommandé'; position:absolute; top:-14px; left:50%; transform:translateX(-50%); background:#f5c842; color:#1a1a1a; font-size:10px; font-weight:800; padding:3px 10px; border-radius:100px; white-space:nowrap; letter-spacing:0.04em; }
          @keyframes barGrow1 { from{width:0} to{width:100%} }
          @keyframes barGrow2 { from{width:0} to{width:11%} }
          @keyframes barGrow3 { from{width:0} to{width:44%} }
          .cmp-bar-1 { animation:barGrow1 1s cubic-bezier(.4,0,.2,1) 0.5s both; }
          .cmp-bar-2 { animation:barGrow2 1s cubic-bezier(.4,0,.2,1) 0.5s both; }
          .cmp-bar-3 { animation:barGrow3 1s cubic-bezier(.4,0,.2,1) 0.5s both; }
          .cmp-cta-btn:hover { background:#1e3d1a !important; transform:translateY(-1px); }
        `}</style>
        <div className="qly-lp-inner" style={{ maxWidth: 1000, margin: '0 auto', padding: '72px 40px' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'5px 16px', background:'rgba(45,90,39,0.1)', color:'#2d5a27', border:'1px solid rgba(45,90,39,0.2)', borderRadius:100, fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:'monospace', marginBottom:12 }}>
              <span style={{ width:6, height:6, background:'#2d5a27', borderRadius:'50%', display:'inline-block' }} />
              Comparaison
            </div>
            <h2 style={{ fontFamily:'Georgia, serif', fontSize:'clamp(22px,2.2vw,32px)', fontWeight:800, lineHeight:1.12, letterSpacing:'-0.8px', color:'#111', marginBottom:8, margin:0 }}>
              Pourquoi choisir <em style={{ fontStyle:'italic', color:'#2d5a27' }}>Qualyse</em> ?
            </h2>
            <p style={{ fontSize:15, color:'#6b6b6b', maxWidth:420, margin:'10px auto 0', lineHeight:1.6 }}>
              Comparé à la méthode manuelle et aux logiciels leaders du marché.
            </p>
          </div>

          {/* Table */}
          <div className="qly-cmp-inner" style={{ background:'#fff', borderRadius:24, overflow:'hidden', boxShadow:'0 8px 48px rgba(0,0,0,0.08)' }}>

            {/* Column headers */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 160px 160px 160px', background:'#fff', borderBottom:'2px solid #f0ede6', paddingTop:18 }}>
              <div style={{ padding:'8px 14px', textAlign:'left', color:'#6b6b6b', fontSize:11, textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:700 }}>
                Fonctionnalité / Critère
              </div>
              <div className="cmp-qhead" style={{ padding:'8px 10px', textAlign:'center', background:'#2d5a27', color:'#fff', fontSize:13, fontWeight:700 }}>
                <span style={{ fontFamily:'Georgia,serif', fontSize:15, fontWeight:800, display:'block', marginBottom:2 }}>Qualyse</span>
                <span style={{ fontSize:10, opacity:0.7, fontWeight:500 }}>Plateforme IA quali</span>
              </div>
              <div style={{ padding:'8px 10px', textAlign:'center', fontSize:13, fontWeight:700, color:'#6b6b6b' }}>
                <span style={{ fontSize:15, color:'#111', display:'block', marginBottom:2, fontWeight:800 }}>Méthode</span>
                <span style={{ fontSize:10, fontWeight:500 }}>manuelle</span>
              </div>
              <div style={{ padding:'8px 10px', textAlign:'center', fontSize:13, fontWeight:700, color:'#6b6b6b' }}>
                <span style={{ fontSize:13, color:'#111', display:'block', marginBottom:2, fontWeight:800 }}>NVivo / ATLAS.ti</span>
                <span style={{ fontSize:10, fontWeight:500 }}>Logiciels spécialisés</span>
              </div>
            </div>

            {/* Data rows */}
            {CMP_ROWS.map((row, i) => (
              <div key={i} className="cmp-row" style={{ display:'grid', gridTemplateColumns:'1fr 160px 160px 160px', borderBottom: i < CMP_ROWS.length - 1 ? '1px solid #f5f2ee' : 'none' }}>
                <div style={{ padding:'7px 14px', display:'flex', alignItems:'center', gap:10, fontWeight:600, color:'#1a1a1a', fontSize:11 }}>
                  <span style={{ width:6, height:6, borderRadius:'50%', background:'#d4edb8', flexShrink:0, display:'inline-block' }} />
                  {row.label}
                </div>
                <div className="cmp-qcell" style={{ padding:'7px 10px', display:'flex', alignItems:'center', justifyContent:'center', background:'#f8fbf6', borderLeft:'1px solid #e8f0e6', borderRight:'1px solid #e8f0e6', transition:'background 0.15s' }}>
                  {row.qualyse === 'yes' ? <CmpIconYes /> : row.qualyse === 'no' ? <CmpIconNo /> : <CmpIconPartial />}
                </div>
                <div style={{ padding:'7px 10px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {row.manuel === 'yes' ? <CmpIconYes /> : row.manuel === 'no' ? <CmpIconNo /> : <CmpIconPartial />}
                </div>
                <div style={{ padding:'7px 10px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {row.nvivo === 'yes' ? <CmpIconYes /> : row.nvivo === 'no' ? <CmpIconNo /> : <CmpIconPartial />}
                </div>
              </div>
            ))}

            {/* Score footer */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 160px 160px 160px', background:'#f5f2ee', borderTop:'2px solid #e8e4db' }}>
              <div style={{ padding:'8px 14px', display:'flex', alignItems:'center', fontSize:12, fontWeight:700, color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.06em' }}>
                Score global
              </div>
              {([
                { num:'9/9', color:'#2d5a27', barCls:'cmp-bar-1' },
                { num:'1/9', color:'#b8b8b8', barCls:'cmp-bar-2' },
                { num:'4/9', color:'#c08a30', barCls:'cmp-bar-3' },
              ] as { num:string; color:string; barCls:string }[]).map((s, i) => (
                <div key={i} style={{ padding:'8px 10px', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:4 }}>
                  <div style={{ fontSize:20, fontWeight:800, letterSpacing:'-0.5px', lineHeight:1, color:s.color }}>{s.num}</div>
                  <div style={{ width:80, height:5, background:'#e2ddd4', borderRadius:5, overflow:'hidden', marginTop:4 }}>
                    <div className={s.barCls} style={{ height:'100%', borderRadius:5, background:s.color, width:0 }} />
                  </div>
                  <div style={{ fontSize:10, color:'#6B7280', fontWeight:500 }}>Fonctionnalités couvertes</div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:16, marginTop:36, flexWrap:'wrap' }}>
            <button
              onClick={scrollToModules}
              className="cmp-cta-btn"
              style={{ padding:'13px 28px', background:'#2d5a27', color:'white', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:8, transition:'background 0.2s, transform 0.15s' }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Essayer Qualyse gratuitement
            </button>
            <span style={{ fontSize:12, color:'#6B7280' }}>Aucune carte requise · Résultats en minutes</span>
          </div>

        </div>
      </section>

      {/* ── Bannière Guide Utilisateur ─────────────────────────────── */}
      <div style={{ background: '#f0f8e8', borderTop: '1px solid #d4edb8', borderBottom: '1px solid #d4edb8', padding: '28px 60px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#2d6614', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
              Guide utilisateur
            </p>
            <p style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.4, margin: 0 }}>
              Pas encore prêt à vous inscrire ?{' '}
              <span style={{ color: '#2d6614' }}>Consultez d&apos;abord notre guide complet.</span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <GuideViewerButton />
            <GuideDownloadButton variant="outline" />
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 6. TARIFICATION                                              */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <section id="tarification" className="border-b border-tq-border">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-tq-text font-sans mb-3">
              Choisissez votre plan
            </h2>
            <p className="text-sm text-tq-muted">
              Commencez gratuitement, évoluez selon vos besoins.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-5" style={{ alignItems: 'start', paddingTop: 20 }}>

            {/* ── PLAN GRATUIT ── */}
            <Card className="relative p-6 flex flex-col border-2 border-tq-border bg-tq-card dark:bg-slate-800 hover:shadow-md transition-shadow">
              <div className="text-3xl mb-3">🆓</div>
              <h3 className="text-lg font-bold text-tq-text font-sans mb-1">Gratuit</h3>
              {/* « Pas d'inscription requise » était trompeur sur ce qui compte :
                  on peut bien arriver, parcourir et DÉPOSER un fichier sans
                  compte, mais lancer un traitement ouvre la fenêtre
                  d'inscription (`openAuthGate` dans les quatre modules). Aucun
                  résultat ne s'obtient sans compte. Ce parcours est VOULU et
                  conservé — c'est donc le texte qui doit le décrire. */}
              <p className="text-xs text-tq-muted mb-1">Sans carte bancaire · compte requis pour lancer</p>
              <div className="mb-4">
                <span className="text-2xl font-bold text-tq-text">0 €</span>
                <span className="text-sm text-tq-muted ml-1">/ Gratuit</span>
              </div>
              <div className="space-y-2.5 flex-1 mb-6">
                {[
                  `${FREE_PLAN.initialCredits} crédits offerts, une seule fois`,
                  `1 transcription jusqu'à ${Math.floor(FREE_PLAN.initialCredits / FREE_PLAN.transcriptionPerMinute)} minutes`,
                  `OU 1 codification jusqu'à ${FREE_PLAN.maxEntretiens} entretiens`,
                  // Décision du 2026-10-12 : l'offre gratuite se limite à ces
                  // deux modules. « Un seul module au choix » ne disait pas
                  // LESQUELS, et laissait donc croire que le rapport d'analyse
                  // en faisait partie — c'était vrai jusqu'à cette date.
                  // L'exclusivité, elle, est déjà portée par le « OU ».
                  'Rapport, note technique et correction : plan Pro',
                ].map((feat) => (
                  <div key={feat} className="flex items-start gap-2 text-sm text-tq-muted">
                    <Check className="h-4 w-4 text-tq-green flex-shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                onClick={scrollToModules}
                className="w-full border-tq-border text-tq-text hover:bg-tq-hover text-sm"
              >
                Commencer gratuitement
              </Button>
            </Card>

            {/* ── PLAN PRO ── */}
            <Card className="pricing-pro-card relative flex flex-col border-2 border-tq-green bg-white dark:bg-slate-800 transition-shadow" style={{ padding: '36px 24px 28px', transform: 'translateY(-20px)', boxShadow: '0 20px 60px rgba(45,90,39,0.18), 0 4px 16px rgba(0,0,0,0.08)', zIndex: Z.content }}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-tq-green text-white border-0 text-xs font-semibold px-3">
                  ⭐ Populaire
                </Badge>
              </div>
              <div className="text-3xl mb-3">⚡</div>
              <h3 className="text-lg font-bold text-tq-text font-sans mb-1">Pro</h3>
              <p className="text-xs text-tq-muted mb-1">Inscription requise</p>
              <div className="mb-4">
                {/* Prix public — SOURCE UNIQUE : src/lib/pricing-display.ts */}
                <span className="text-2xl font-bold text-tq-text">{REFERENCE_PRICE.amount}</span>
                <span className="text-sm text-tq-muted ml-1">{REFERENCE_PRICE.period}</span>
              </div>
              <div className="space-y-2.5 flex-1 mb-6">
                {[
                  // D-025 : une souscription = une PÉRIODE de 30 jours. Il n'y a
                  // aucun prélèvement récurrent, donc pas de « par mois ». Et les
                  // crédits non consommés sont PERDUS à l'échéance — l'ancienne
                  // mention « valables jusqu'à épuisement » promettait l'inverse
                  // de ce que les CGU engagent et de ce que le code applique.
                  // `formatCreditCount` et non `toLocaleString` : Node sépare les
                  // milliers par U+202F, dont la valeur dépend de la version
                  // d'ICU — un rendu serveur réhydraté par le navigateur peut
                  // alors différer. Même raison que `payment-confirmation.ts`.
                  `${formatCreditCount(PLAN_CREDITS.pro)} crédits par souscription`,
                  `Valables ${PRO_PERIOD_DAYS} jours, non reportés`,
                  'Accès à tous les modules',
                  // Inventaire des boutons réellement présents dans les quatre
                  // modules : Word (note technique, transcription, verbatim
                  // corrigé, rapport), Excel (codification), TXT
                  // (transcription, verbatim corrigé). AUCUN export PDF —
                  // `pdf-parse` et `pdfjs-dist` ne servent qu'à LIRE les
                  // fichiers déposés.
                  'Export Word, Excel et TXT',
                  'Nouvelle souscription à tout moment',
                ].map((feat) => (
                  <div key={feat} className="flex items-start gap-2 text-sm text-tq-muted">
                    <Check className="h-4 w-4 text-tq-green flex-shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => {
                  // La destination est transmise à la modale : après connexion,
                  // l'utilisateur est envoyé directement au Hub (sinon il reste
                  // sur la landing et doit recliquer sur le CTA).
                  if (!user) { openAuthModal('register', '/payment/hub'); return }
                  window.location.href = '/payment/hub'
                }}
                className="w-full bg-tq-green hover:bg-tq-green/90 text-white text-sm font-semibold"
              >
                Choisir le plan Pro
              </Button>
            </Card>

            {/* ── PLAN ÉQUIPES (désactivé) ── */}
            <Card className="relative p-6 flex flex-col border-2 border-tq-border bg-tq-card dark:bg-slate-800 opacity-50">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-slate-400 text-white border-0 text-xs font-semibold px-3">
                  Bientôt disponible
                </Badge>
              </div>
              <div className="text-3xl mb-3">🏢</div>
              <h3 className="text-lg font-bold text-tq-text font-sans mb-1">Équipes</h3>
              <p className="text-xs text-tq-muted mb-4">
                Espace de travail collaboratif pour les équipes et entreprises
              </p>
              <div className="space-y-2.5 flex-1 mb-6">
                {[
                  'Tout le plan Pro',
                  'Multi-utilisateurs',
                  'Gestion de projets',
                  'Support dédié',
                ].map((feat) => (
                  <div key={feat} className="flex items-start gap-2 text-sm text-tq-muted">
                    <Check className="h-4 w-4 text-tq-muted flex-shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                disabled
                className="w-full border-tq-border text-tq-muted text-sm cursor-not-allowed mb-3"
              >
                Bientôt disponible
              </Button>
              <button
                onClick={() => scrollToSection('footer')}
                className="text-xs text-tq-muted hover:text-tq-text underline underline-offset-2 text-center transition-colors"
              >
                Contactez-nous pour être notifié
              </button>
            </Card>

          </div>

          <p className="text-center text-sm text-tq-muted mt-8">
            Commencez gratuitement aujourd&apos;hui. Aucune carte bancaire requise.
          </p>
        </div>
      </section>

      {/* Section TÉMOIGNAGES retirée : citations nominatives attribuées à des
          personnes et à des institutions, sans accord ni existence vérifiable.
          Elle n'est remplacée par rien — d'autres citations poseraient
          exactement le même problème. */}

      <FaqSection />



      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 8. FOOTER                                                    */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* Pre-footer CTA band */}
      <div className="prefooter" style={{ background:'#2d5a27', padding:'56px 60px', position:'relative', overflow:'hidden' }}>
        <style>{`
          .prefooter::before { content:''; position:absolute; top:-80px; right:-80px; width:320px; height:320px; border-radius:50%; background:rgba(255,255,255,0.05); pointer-events:none; }
          .prefooter::after  { content:''; position:absolute; bottom:-60px; left:10%; width:200px; height:200px; border-radius:50%; background:rgba(255,255,255,0.04); pointer-events:none; }
          .ft-nav-link { font-size:13px; color:rgba(255,255,255,0.6); display:inline-flex; align-items:center; cursor:pointer; background:none; border:none; padding:0; font-family:inherit; transition:color 0.15s; }
          .ft-nav-link::before { content:''; width:0; height:1px; background:#72b84e; transition:width 0.2s; display:inline-block; }
          .ft-nav-link:hover { color:#fff; }
          .ft-nav-link:hover::before { width:12px; margin-right:6px; }
          .ft-social { width:36px; height:36px; border-radius:8px; background:rgba(255,255,255,0.07); border:1px solid rgba(255,255,255,0.1); display:flex; align-items:center; justify-content:center; cursor:pointer; transition:background 0.2s, border-color 0.2s; }
          .ft-social:hover { background:#3a7a32; border-color:#3a7a32; }
          .ft-btn-white { transition:transform 0.15s, box-shadow 0.15s; }
          .ft-btn-white:hover { transform:translateY(-2px); box-shadow:0 8px 20px rgba(0,0,0,0.18); }
          .ft-btn-outline { transition:border-color 0.2s, background 0.2s; }
          .ft-btn-outline:hover { border-color:rgba(255,255,255,1) !important; background:rgba(255,255,255,0.08) !important; }
          @keyframes ft-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
          .ft-rgpd-dot { width:6px; height:6px; border-radius:50%; background:#72b84e; animation:ft-pulse 2s infinite; flex-shrink:0; }
        `}</style>
        <div style={{ maxWidth:1100, margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'space-between', gap:40, flexWrap:'wrap', position:'relative', zIndex:Z.content }}>
          <div>
            <h3 className="prefooter-h3" style={{ fontFamily:'Georgia,serif', fontSize:32, fontWeight:800, color:'#fff', lineHeight:1.15, letterSpacing:'-0.5px', marginBottom:10 }}>
              Prêt à accélérer<br/>
              <em style={{ fontStyle:'italic', opacity:0.8 }}>votre recherche qualitative ?</em>
            </h3>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.65)', lineHeight:1.6 }}>
              Commencez gratuitement. Résultats en quelques minutes.
            </p>
          </div>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', flexShrink:0 }}>
            <button onClick={scrollToModules} className="ft-btn-white"
              style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'13px 24px', background:'#fff', color:'#2d5a27', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="#2d5a27" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Commencer gratuitement
            </button>
            <button onClick={() => scrollToSection('demo')} className="ft-btn-outline"
              style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'12px 22px', background:'transparent', color:'#fff', border:'1.5px solid rgba(255,255,255,0.4)', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5" stroke="white" strokeWidth="1.5"/><path d="M5.5 5.5l4 1.5-4 1.5V5.5z" fill="white"/></svg>
              Voir la démo
            </button>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <footer id="footer" className="qly-footer" style={{ background:'#111', color:'#fff', padding:'60px 60px 0' }}>
        <div className="qly-footer-grid" style={{ maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:48, paddingBottom:48, borderBottom:'1px solid rgba(255,255,255,0.08)' }}>

          {/* Brand col */}
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <div style={{ width:36, height:36, background:'#2d5a27', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Georgia,serif', fontSize:18, fontWeight:800, color:'#fff' }}>Q</div>
              <span style={{ fontFamily:'Georgia,serif', fontSize:20, fontWeight:800, color:'#fff' }}>Qualyse</span>
            </div>
            <p style={{ fontSize:13, color:'rgba(255,255,255,0.5)', lineHeight:1.7, marginBottom:24, maxWidth:240 }}>
              Plateforme d&apos;analyse qualitative assistée par IA — de la transcription au rapport final, en un seul espace de travail.
            </p>
            <div style={{ display:'flex', gap:10, marginBottom:28 }}>
              {([
                <svg key="li" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 5.5h2V13H3V5.5zM4 4.5a1 1 0 100-2 1 1 0 000 2zM6.5 5.5h1.8v1s.7-1.2 2.4-1.2c1.9 0 2.8 1.2 2.8 3.3V13H11.5V9c0-1-.3-1.8-1.3-1.8s-1.7.9-1.7 1.9V13H6.5V5.5z" fill="rgba(255,255,255,0.7)"/></svg>,
                <svg key="wa" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2C4.69 2 2 4.69 2 8c0 1.07.28 2.07.77 2.93L2 14l3.2-.74A5.97 5.97 0 008 14c3.31 0 6-2.69 6-6s-2.69-6-6-6zm2.9 8.1c-.12.34-.72.66-1 .68-.26.02-.5.12-1.7-.35-1.43-.57-2.35-2-2.42-2.1-.07-.1-.58-.77-.58-1.47s.37-1.04.5-1.19c.13-.15.28-.18.38-.18h.27c.09 0 .21-.04.33.25l.47 1.14c.04.1.07.22.01.34l-.17.25-.22.27c.07.12.35.55.75.9.52.45 1 .61 1.13.68.14.07.22.06.3-.04l.38-.45c.1-.12.2-.1.33-.06l1.04.49c.12.06.2.1.23.15.03.05.03.33-.1.67z" fill="rgba(255,255,255,0.7)"/></svg>,
                <svg key="em" width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="4" width="12" height="9" rx="1.5" stroke="rgba(255,255,255,0.7)" strokeWidth="1.3"/><path d="M2 5l6 4.5L14 5" stroke="rgba(255,255,255,0.7)" strokeWidth="1.3" strokeLinecap="round"/></svg>,
              ] as React.ReactNode[]).map((icon, i) => (
                <button key={i} className="ft-social">{icon}</button>
              ))}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {/* Trois affirmations corrigées, chacune pour une raison distincte :
                  · « supprimées après traitement » — FAUX pour les résultats :
                    transcriptions et documents sont CONSERVÉS par conception,
                    c'est l'historique du tableau de bord. Seul l'audio déposé
                    est purgé (`purgeR2OrphanFiles`, 24 h) ;
                  · « Aucune donnée partagée » — FAUX, et contredit la propre
                    politique de confidentialité du site, qui déclare OpenAI et
                    Anthropic comme sous-traitants (/privacy) ;
                  · « Hébergement sécurisé EU » — INVÉRIFIABLE depuis le dépôt :
                    aucune région n'y est déclarée pour Railway, Supabase ou R2.
                  « Conforme RGPD » est conservé : c'est une déclaration de
                  l'éditeur, adossée à /privacy, pas une mesure inventée. */}
              {['Audio supprimé après traitement', 'Sous-traitants IA listés dans la politique de confidentialité', 'Conforme RGPD'].map((txt, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'rgba(255,255,255,0.45)' }}>
                  <div style={{ width:5, height:5, borderRadius:'50%', background:'#72b84e', flexShrink:0 }} />
                  {txt}
                </div>
              ))}
            </div>
          </div>

          {/* Nav cols */}
          {([
            { title:'Plateforme', links:[{l:'Notes techniques'},{l:'Transcription',badge:'New'},{l:'Codification'},{l:'Rapports'},{l:'Fonctionnalités'},{l:'Tarifs',href:'/pricing'}] },
            { title:'Ressources',  links:[{l:'Lire le Guide',href:'/guide'},{l:'Télécharger le Guide (PDF)',href:'/api/download/guide'},{l:'Documentation'},{l:'FAQ'},{l:'Blog'},{l:'Démonstration'},{l:"Cas d'usage"},{l:'Sitemap',href:'/sitemap.xml'}] },
            { title:'Entreprise',  links:[{l:'À propos'},{l:'Contact',href:'/contact'},{l:'Confidentialité',href:'/privacy'},{l:'RGPD',href:'/privacy'},{l:'CGU',href:'/terms'}] },
          ] as {title:string; links:{l:string;badge?:string;href?:string;download?:boolean}[]}[]).map((col, ci) => (
            <div key={ci}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(255,255,255,0.35)', marginBottom:18, fontFamily:'monospace' }}>
                {col.title}
              </div>
              <ul style={{ listStyle:'none', display:'flex', flexDirection:'column', gap:10, padding:0 }}>
                {col.links.map((link, li) => (
                  <li key={li}>
                    {link.href ? (
                      <a href={link.href} className="ft-nav-link" style={{ textDecoration:'none' }} download={link.download ? '' : undefined}>
                        {link.l}
                        {link.badge && <span style={{ display:'inline-block', padding:'1px 6px', background:'#2d5a27', color:'#72b84e', fontSize:9, fontWeight:800, borderRadius:4, letterSpacing:'0.05em', textTransform:'uppercase', marginLeft:6 }}>{link.badge}</span>}
                      </a>
                    ) : (
                      <button className="ft-nav-link">
                        {link.l}
                        {link.badge && <span style={{ display:'inline-block', padding:'1px 6px', background:'#2d5a27', color:'#72b84e', fontSize:9, fontWeight:800, borderRadius:4, letterSpacing:'0.05em', textTransform:'uppercase', marginLeft:6 }}>{link.badge}</span>}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div style={{ maxWidth:1100, margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 0', gap:16, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16, fontSize:12, color:'rgba(255,255,255,0.3)', flexWrap:'wrap' }}>
            <span>© 2026 Qualyse. Tous droits réservés. Développé et opéré par Prism Research SARL — RCCM : CI-ABJ-03-2026-B12-00535</span>
            <span style={{ opacity:0.2 }}>|</span>
            <a href="/terms" className="ft-nav-link" style={{ fontSize:12, color:'rgba(255,255,255,0.3)', textDecoration:'none' }}>Conditions générales</a>
            <span style={{ opacity:0.2 }}>|</span>
            <a href="/privacy" className="ft-nav-link" style={{ fontSize:12, color:'rgba(255,255,255,0.3)', textDecoration:'none' }}>Politique de confidentialité</a>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'rgba(255,255,255,0.25)' }}>
            <div className="ft-rgpd-dot" />
            Données sécurisées · Conforme RGPD
          </div>
        </div>
      </footer>


      {/* ═══════════════════════════════════════════════════════════ */}
      {/* TABLEAU DE BORD (reconstruit — composant autonome)          */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <DashboardSheet
        open={showDashboard}
        onOpenChange={setShowDashboard}
        initialTab={dashboardInitialTab}
        onRecharge={goToPricing}
        onCreditsLoaded={(c) => setProfile((p) => (p ? { ...p, credits: c } : p))}
      />

      {/* ── Freemium modals ─── */}
      {/* Popup d'inscription obligatoire avant tout traitement (visiteur non connecté).
          Les boutons ouvrent le formulaire en modale in-page : les fichiers déjà
          uploadés sont conservés et le traitement se relance automatiquement. */}
      <SignupGateModal
        open={showGateModal}
        onClose={() => setShowGateModal(false)}
        onRegister={() => { setShowGateModal(false); openAuthModal('register') }}
        onLogin={() => { setShowGateModal(false); openAuthModal('login') }}
        onAuthSuccess={handleAuthSuccess}
      />
      <AuthModal
        open={showAuthModal}
        defaultTab={authModalTab}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        redirectAfterSuccess={authRedirectAfterSuccess}
      />
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSignUp={() => { setShowUpgradeModal(false); openAuthModal('register', '/pricing') }}
      />

    </div>

    {/* ── UserPanel — outside main div so it renders over everything ── */}
    {user && profile && (
      <UserPanel
        user={{ email: user.email, full_name: profile.full_name }}
        credits={profile.credits}
        plan={profile.plan}
        onLogout={handleLogout}
        onOpenDashboard={() => openDashboard('overview')}
        onOpenAdmin={() => setShowAdminPanel(true)}
      />
    )}

    <AdminPanel open={showAdminPanel} onClose={() => setShowAdminPanel(false)} />
    <OnboardingTour />

    </FreemiumProvider>
  )
}
